import React, {useState, useEffect} from 'react';
import {useIntl} from 'react-intl';
import InvoiceDetailsForm from '../components/invoice_details_form';
import {
	GroupingBox,
	GroupingBoxSection,
	GroupingBoxHeadline,
	RadioButtonHeadline,
	GroupingBoxSectionContent,
	RadioButtonWrapper,
	RadioButtonSection,
} from '../../../../styles/v2/invoice_modals_v2_styled';
import Dropdown from '../../../../forecast-app/shared/components/dropdowns/dropdown';
import RadioButton from '../../../../components/new-ui/radio_button';
import {
	TIME_REG_GROUPINGS,
	EXPENSE_GROUPINGS,
} from '../../../../components/insights/invoicing/line-generation/invoicing_generate_lines';
import FixedPriceDeliverablesSection from './fixed_price_deliverables_section';
import DatePicker from '../../../../forecast-app/shared/components/date-picker/date_picker_v3';
import {BUDGET_TYPE, DATE_PICKER_STYLE} from '../../../../constants';
import {hasFeatureFlag} from '../../../../forecast-app/shared/util/FeatureUtil';
import {WorkDetailOverlapNotice} from './work_detail_overlap_notice';

const InvoiceSettingsAndLineGeneration = ({
	setInvoice,
	invoice,
	selectedProjectsIds,
	currenciesDropdownOptions,
	projects,
	company,
	isEditing,
	config,
	setConfig,
	setHasError,
	isBulk,
	isProjectInvoicing,
	invoiceLines,
	setInvoiceLines,
	workDetailOverlaps = [],
}) => {
	let billableHourGroupingOptions = [];
	let expenseGroupingOptions = [];
	const intl = useIntl();
	Object.keys(TIME_REG_GROUPINGS)
		.filter(timeRegGrouping => !isProjectInvoicing || timeRegGrouping !== 'PROJECT')
		.forEach(timeRegGrouping => {
			let label = '';
			switch (timeRegGrouping) {
				case 'NO_GROUPING':
					label = intl.formatMessage({id: 'project_sprints.group_by_people_disable'});
					break;
				case 'PROJECT':
					label = intl.formatMessage({id: 'common.project'});
					break;
				case 'TASK':
					label = intl.formatMessage({id: 'common.task'});
					break;
				case 'DONE_TASK':
					label = intl.formatMessage({id: 'invoice.done_task'});
					break;
				case 'PERSON':
					label = intl.formatMessage({id: 'common.person'});
					break;
				case 'ROLE':
					label = intl.formatMessage({id: 'common.role'});
					break;
				case 'PHASE':
					label = intl.formatMessage({id: 'common.phase'});
					break;
				case 'SPRINTS':
					label = intl.formatMessage({id: 'common.sprints'});
					break;
				case 'NO_GROUPING_NOTES':
					label = intl.formatMessage({id: 'invoice.no_grouping_notes'});
					break;
			}
			billableHourGroupingOptions.push({value: timeRegGrouping, label: label});
		});
	Object.keys(EXPENSE_GROUPINGS)
		.filter(expenseGrouping => !isProjectInvoicing || expenseGrouping !== 'PROJECT')
		.forEach(expenseGrouping => {
			let label = '';
			switch (expenseGrouping) {
				case 'NO_GROUPING':
					label = intl.formatMessage({id: 'project_sprints.group_by_people_disable'});
					break;
				case 'PROJECT':
					label = intl.formatMessage({id: 'common.project'});
					break;
				case 'PERSON':
					label = intl.formatMessage({id: 'common.person'});
					break;
				case 'PHASE':
					label = intl.formatMessage({id: 'common.phase'});
					break;
			}
			expenseGroupingOptions.push({value: expenseGrouping, label: label});
		});

	const [timeRegGrouping, setTimeRegGrouping] = useState(config.timeRegGrouping);
	const [expenseGrouping, setExpenseGrouping] = useState(config.expenseGrouping);
	const [includeExpenses, setIncludeExpenses] = useState(config.includeExpenses);
	const [includeWorkDetail, setIncludeWorkDetail] = useState(config.includeWorkDetail);
	const [workDetailStartDate, setWorkDetailStartDate] = useState(config.workDetailStartDate);
	const [workDetailEndDate, setWorkDetailEndDate] = useState(config.workDetailEndDate);
	// Tells the modal that the window it reads back is the operator's and not its own default, so it
	// stops recomputing one from the project selection. Latched, never cleared: stepping back and
	// forward remounts this step, and the operator's window has to survive that the same way their
	// removed rows do.
	const [workDetailWindowTouched, setWorkDetailWindowTouched] = useState(config.workDetailWindowTouched);
	useEffect(() => {
		setConfig({
			...config,
			timeRegGrouping,
			expenseGrouping,
			includeExpenses,
			includeWorkDetail,
			workDetailStartDate,
			workDetailEndDate,
			workDetailWindowTouched,
			selectedProjectsIds,
		});
	}, [
		timeRegGrouping,
		expenseGrouping,
		includeExpenses,
		includeWorkDetail,
		workDetailStartDate,
		workDetailEndDate,
		workDetailWindowTouched,
	]);

	const workDetailEnabled = hasFeatureFlag('work_detail_on_fixed_price_invoices');

	/**
	 * Work detail is offered only when every selected project is fixed-price.
	 *
	 * On a Time & Materials project the priced lines ARE the time registrations, so a "work completed,
	 * not charged" section beneath them lists the very hours the invoice is charging for — and lists
	 * more of them, because the snapshot reads the project's whole time-registration history rather
	 * than the uninvoiced pool, pulling in work already billed on earlier invoices.
	 *
	 * Every selected project rather than any, because the section is one table over the whole invoice:
	 * with a mixed selection the T&M half would double-report however the fixed-price half was
	 * presented. An empty selection offers nothing, since there is no work to describe.
	 */
	const selectedProjects = projects.filter(project => selectedProjectsIds.includes(project.id));
	const isFixedPriceInvoice =
		selectedProjects.length > 0 &&
		selectedProjects.every(
			project => project.budgetType === BUDGET_TYPE.FIXED_PRICE || project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2
		);
	const workDetailAvailable = workDetailEnabled && isFixedPriceInvoice;

	// Changing the project selection can withdraw work detail after the operator already turned it on,
	// and the parent deliberately carries includeWorkDetail across a selection change. Hiding the
	// control is therefore not enough on its own — without this the choice stays active behind a
	// control that is no longer there, and the section still reaches the saved invoice.
	useEffect(() => {
		if (!workDetailAvailable && includeWorkDetail) {
			setIncludeWorkDetail(false);
		}
	}, [workDetailAvailable, includeWorkDetail]);

	/**
	 * Deliberately NOT written to localStorage, unlike includeExpenses beside it. This control
	 * decides what a client sees on a document, so remembering it per browser would silently apply
	 * one invoice's choice to the next.
	 */
	const onIncludeWorkDetailChange = value => setIncludeWorkDetail(value);

	const onWorkDetailDateRangeChange = (newStartDate, newEndDate) => {
		setWorkDetailStartDate(newStartDate);
		setWorkDetailEndDate(newEndDate);
		setWorkDetailWindowTouched(true);
	};

	const onTimeRegGroupingChange = value => {
		setTimeRegGrouping(value);
		localStorage.setItem('invoicing_time_reg_grouping', value);
	};

	const onExpenseGroupingChange = value => {
		setExpenseGrouping(value);
		localStorage.setItem('invoicing_expense_grouping', value);
	};

	const onIncludeExpensesChange = value => {
		setIncludeExpenses(value);
		localStorage.setItem('invoicing_include_expenses', value);
	};

	return (
		<>
			<InvoiceDetailsForm
				isEditing={isEditing}
				invoice={invoice}
				setInvoice={setInvoice}
				currenciesDropdownOptions={currenciesDropdownOptions}
				isCreatingBlankInvoice={false}
				currency={company.currency}
				selectedProjectsIds={selectedProjectsIds}
				projects={projects}
				setHasError={setHasError}
				company={company}
				isBulk={isBulk}
			/>
			<GroupingBox data-userpilot={'grouping-options'}>
				<GroupingBoxSection>
					<GroupingBoxHeadline>{intl.formatMessage({id: 'project_budget.billable_hours'})}</GroupingBoxHeadline>
					<GroupingBoxSectionContent>
						<Dropdown
							onChange={option => onTimeRegGroupingChange(option.value)}
							options={billableHourGroupingOptions}
							value={
								billableHourGroupingOptions.some(option => option.value === timeRegGrouping)
									? timeRegGrouping
									: TIME_REG_GROUPINGS.NO_GROUPING
							}
							label={intl.formatMessage({id: 'common.group-by'})}
							inputCy={'time-reg-grouping'}
						/>
					</GroupingBoxSectionContent>
				</GroupingBoxSection>
				<GroupingBoxSection>
					<GroupingBoxHeadline>{intl.formatMessage({id: 'invoicing.include_expenses'})}</GroupingBoxHeadline>
					<GroupingBoxSectionContent>
						<RadioButtonSection>
							<RadioButtonHeadline>{intl.formatMessage({id: 'common.all_projects'})}</RadioButtonHeadline>
							<RadioButtonWrapper>
								<RadioButton
									onSelected={onIncludeExpensesChange.bind(this)}
									value={true}
									stateValue={includeExpenses}
									text={intl.formatMessage({id: 'common.yes'})}
									cy={'include-expenses-yes'}
								/>
								<RadioButton
									onSelected={onIncludeExpensesChange.bind(this)}
									value={false}
									stateValue={includeExpenses}
									text={intl.formatMessage({id: 'common.no'})}
									cy={'include-expenses-no'}
								/>
							</RadioButtonWrapper>
						</RadioButtonSection>
						<Dropdown
							onChange={option => onExpenseGroupingChange(option.value)}
							options={expenseGroupingOptions}
							value={
								expenseGroupingOptions.some(option => option.value === expenseGrouping)
									? expenseGrouping
									: EXPENSE_GROUPINGS.NO_GROUPING
							}
							label={intl.formatMessage({id: 'common.group-by'})}
							inputCy={'expense-grouping'}
						/>
					</GroupingBoxSectionContent>
				</GroupingBoxSection>
				{workDetailAvailable && (
					<GroupingBoxSection>
						<GroupingBoxHeadline>
							{intl.formatMessage({id: 'invoicing.work_detail.include_label'})}
						</GroupingBoxHeadline>
						<GroupingBoxSectionContent>
							<RadioButtonSection>
								<RadioButtonWrapper>
									<RadioButton
										onSelected={onIncludeWorkDetailChange}
										value={true}
										stateValue={includeWorkDetail}
										text={intl.formatMessage({id: 'common.yes'})}
										cy={'include-work-detail-yes'}
									/>
									<RadioButton
										onSelected={onIncludeWorkDetailChange}
										value={false}
										stateValue={includeWorkDetail}
										text={intl.formatMessage({id: 'common.no'})}
										cy={'include-work-detail-no'}
									/>
								</RadioButtonWrapper>
							</RadioButtonSection>
							{includeWorkDetail && (
								<div data-cy={'work-detail-period'}>
									<RadioButtonHeadline>
										{intl.formatMessage({id: 'invoicing.work_detail.period_label'})}
									</RadioButtonHeadline>
									{/* Separate from the billing date range on the previous step, which is
									    hidden unless the project is Time & Materials and which does not
									    affect a fixed-price project's uninvoiced figures. The two are not
									    wired together on purpose. */}
									<DatePicker
										isNewDateRange={true}
										startDate={workDetailStartDate}
										endDate={workDetailEndDate}
										handleDateRangeChange={onWorkDetailDateRangeChange}
										colorInherited={true}
										compactShowYear={true}
										datePickerStyle={DATE_PICKER_STYLE.STANDARD}
										clearable={false}
									/>
									{/* Beside the control that causes it, so widening the period back over an
									    invoice the client has already seen is answerable here rather than a
									    step away. Repeated on the review step for a last look before saving. */}
									<WorkDetailOverlapNotice overlaps={workDetailOverlaps} />
								</div>
							)}
						</GroupingBoxSectionContent>
					</GroupingBoxSection>
				)}
				{!isBulk && (
					<FixedPriceDeliverablesSection
						projects={projects.filter(project => selectedProjectsIds.includes(project.id))}
						invoice={invoice}
						company={company}
						invoiceLines={invoiceLines}
						setInvoiceLines={setInvoiceLines}
					/>
				)}
			</GroupingBox>
		</>
	);
};

export default InvoiceSettingsAndLineGeneration;
